import { ReactLazyPreload } from '@creator/ui/utils/react';
import { triggerPageViewAds } from '@src/components/Ads/PlaywireRamp';
import Header from '@src/components/Header/Header';
import usePageTitle from '@src/hooks/usePageTitle';
import { ensureTokenName, getDomainConfigValue } from '@src/model/config/helpers';
import { useStoreActions, useStoreState } from '@src/model/hooks';
import { hasActiveFlair } from '@src/model/token/helpers';
import { getUserId } from '@src/model/user/helpers';
import { pushToDataLayer } from '@src/services/gtmService';
import storageService from '@src/services/storage-service';
import { visit } from '@src/store/users/users';
import { storeEqualityFn } from '@src/utils/object-utils/object-utils';
import { isCurrentPathFirstEverVisitPath } from '@src/utils/url-utils/url-utils';
import type { FC } from 'react';
import { Activity, memo, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import TokenPagePostsTab from './PostsTab/PostsTab';
import PostTab from './PostTab/PostTab';

const TokenPageHoldersTab = ReactLazyPreload(() => import('./HoldersTab/HoldersTab'));
const LeaderboardTab = ReactLazyPreload(() => import('./LeaderboardTab/LeaderboardTab'));
const VideoTab = ReactLazyPreload(() => import('./PostTab/VideoTab'));
const ReportTab = ReactLazyPreload(() => import('./ActivityTab/ReportTab/ReportTab'));
const TokenPageAboutTab = ReactLazyPreload(() => import('./AboutTab/AboutTab'));

type TokenPageTab = 'posts' | 'post' | 'about' | 'holders' | 'reports' | 'video' | 'leaderboard';

interface TokenPageProps {
    tokenName?: string;
}

const TokenPage: FC<TokenPageProps> = props => {
    const { tokenSlugOrName, identifier, tab: tabUrlParam, videoIdentifier, commentId } = useParams<Record<string, string>>();
    const tokenName = getTokenName();

    const navigate = useNavigate();
    const location = useLocation();

    const isFirstSetReferredTokenName = useRef(true);
    const loadToken = useStoreActions(actions => actions.token.loadToken);
    const token = useStoreState(state => state.token.getToken(tokenName), storeEqualityFn);
    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);

    const tab = getTab();

    usePageTitle(getPageTitle());

    function getTokenName() {
        if (props.tokenName) return props.tokenName;
        if (tokenSlugOrName === 'video') return getDomainConfigValue('defaultTokenName');
        if (tokenSlugOrName) return ensureTokenName(tokenSlugOrName);
        return getDomainConfigValue('defaultTokenName');
    }

    useEffect(() => {
        if (!token) return;
        if (tab !== 'post') {
            trackPageView();
            triggerPageViewAds('tokenPage');
        }
    }, [location.pathname, tab]);

    useEffect(() => {
        _loadToken();
    }, [tokenName]);

    useEffect(() => {
        // Track visits to the token page or its subpages
        if (isLoggedIn) visit({ tokenName, userId: getUserId() });

        if (token && isFirstSetReferredTokenName.current) {
            _setReferredTokenName();
            isFirstSetReferredTokenName.current = false;
        }
    }, [token]);

    function getPageTitle() {
        if (!token) return '';
        if (tab === 'post' && identifier) return '';
        return `${token?.title} • News and Community`;
    }

    function trackPageView() {
        pushToDataLayer({ event: 'non__article_view', path: location.pathname });
    }

    function getTab(): TokenPageTab {
        if (tabUrlParam) return tabUrlParam as TokenPageTab;
        if (videoIdentifier) return 'video';
        if (token && identifier && !hasActiveFlair(identifier, tokenName)) return 'post';
        return 'posts';
    }

    async function _loadToken(): Promise<void> {
        console.log(`loading token ${tokenName}`);
        try {
            await loadToken(tokenName);
        } catch (e: any) {
            if (e.message.includes('not found')) navigate('/bbs/404');
        }
    }

    function _setReferredTokenName() {
        if (!token) return;
        const referredBy = storageService.localStorage.get('referredTokenName');
        if (!referredBy && isCurrentPathFirstEverVisitPath()) storageService.localStorage.set('referredTokenName', token.tokenName);
    }

    function renderTokenPageContent() {
        if (!token) return null;

        return (
            <>
                <Activity mode={tab === 'posts' ? 'visible' : 'hidden'}>
                    <TokenPagePostsTab flairId={identifier} tokenName={tokenName} />
                </Activity>
                <Activity mode={tab === 'about' ? 'visible' : 'hidden'}>
                    <TokenPageAboutTab tokenName={tokenName} />
                </Activity>
                <Activity mode={tab === 'holders' ? 'visible' : 'hidden'}>
                    <TokenPageHoldersTab tokenName={tokenName} />
                </Activity>
                <Activity mode={tab === 'reports' ? 'visible' : 'hidden'}>
                    <ReportTab tokenName={tokenName} />
                </Activity>
                <Activity mode={tab === 'leaderboard' ? 'visible' : 'hidden'}>
                    <LeaderboardTab tokenName={tokenName} />
                </Activity>
                {tab === 'video' && videoIdentifier && <VideoTab tokenName={tokenName} identifier={videoIdentifier} />}
                {tab === 'post' && identifier && <PostTab tokenName={tokenName} identifier={identifier} commentId={commentId} />}
            </>
        );
    }

    return (
        <div className="page">
            <Header tokenName={tokenName} />
            <div className="space-y-4">{renderTokenPageContent()}</div>
        </div>
    );
};

export default memo(TokenPage);
